import { useContext, useEffect, useMemo, useState } from "react";
import { ObjectSchema, number, object } from "yup";
import { AvisConstants } from "../../../../data/avisConstants";
import {
  BlankMediaTypeApplicationListFilter,
  BlankMediaTypeCommonDataViewModel,
  ItemViewModel,
} from "../../../../generated/data-contracts";
import { ILinkValue } from "../../../../models";
import {
  BlankMediaUrls,
  BlankMediaUtils,
  IAxiosError,
  TableUtils,
  api,
  parseFloatOrDefault,
} from "../../../../utils";
import { AvisForm } from "../../../shared/components";
import { AvisDescriptiveText } from "../../../shared/components/form";
import {
  AvisFilterableTable,
  IColumnConfig,
} from "../../../shared/components/table";
import { AvisContext } from "../../../shared/context";
import { BlankMediaTypeApplicationsFilterForm } from "./filter/BlankMediaTypeApplicationsFilterForm";

export function BlankMediaApplications() {
  const { res } = useContext(AvisContext);
  const [error, setError] = useState<IAxiosError>();
  const [statuses, setStatuses] = useState<ItemViewModel[]>();
  const [useTypes, setUseTypes] = useState<ItemViewModel[]>();
  const [isLoadingStatuses, setIsLoadingStatuses] = useState<boolean>(true);
  const [isLoadingUseTypes, setIsLoadingUseTypes] = useState<boolean>(true);
  const defaultFilterValues: BlankMediaTypeApplicationListFilter = {
    status: undefined,
    useType: undefined,
    pageIndex: 0,
    pageSize: 10,
  };

  useEffect(() => {
    api
      .classifiersGetBlankMediaUsesForFilter()
      .then((res) => {
        setUseTypes(res.data);
      })
      .catch(setError)
      .finally(() => setIsLoadingUseTypes(false));
    api
      .classifiersGetPortalReportStatusTypes()
      .then((res) => {
        setStatuses(res.data);
      })
      .catch(setError)
      .finally(() => setIsLoadingStatuses(false));
  }, []);

  const validationSchema: ObjectSchema<BlankMediaTypeApplicationListFilter> =
    object({
      status: number(),
      useType: number(),
      pageIndex: number().min(0),
      pageSize: number().min(1),
    });

  const columnMetadata: IColumnConfig[] = useMemo(() => {
    return [
      TableUtils.columnConfigDateTime<BlankMediaTypeCommonDataViewModel>(
        "documentDate",
        res("label_date"),
        undefined,
        115,
      ),
      TableUtils.columnConfigLink<BlankMediaTypeCommonDataViewModel>(
        "documentNumber",
        res("label_Number"),
        (rowData) => {
          return {
            title: rowData.documentNumber,
            url: BlankMediaUtils.getUrlWithParams(
              BlankMediaUrls.applicationViewWithId,
              {
                applicationId: rowData.blankMediaTypeId,
                applicationType: rowData.useType,
              },
            ),
          } as ILinkValue;
        },
        150,
      ),
      TableUtils.columnConfigPeriod<BlankMediaTypeCommonDataViewModel>(
        "dateFrom",
        "dateTo",
        res("label_ReportPeriod"),
        undefined,
        AvisConstants.table_column_period_width,
      ),
      TableUtils.columnConfigNested<BlankMediaTypeCommonDataViewModel>(
        "portalReportStatusType",
        "shortName",
        res("label_Status"),
      ),
      TableUtils.columnConfigDownloadInvoicePdfXml<BlankMediaTypeCommonDataViewModel>(
        "invoice",
        res("label_Invoice"),
        (rowData) =>
          api.blankMediaUsersGetInvoicePdf(rowData.invoice?.uniqueId ?? "", {
            format: "arraybuffer",
          }),
        (rowData) =>
          api.blankMediaUsersGetInvoiceXml(rowData.invoice?.uniqueId ?? "", {
            format: "arraybuffer",
          }),
        (rowData) => rowData.invoice?.number,
      ),
      TableUtils.columnConfigNested<BlankMediaTypeCommonDataViewModel>(
        "invoice",
        "amountToPay",
        res("blankMedia_Application_label_AmountToPay"),
        undefined,
        (value) => parseFloatOrDefault(value).toFixed(2),
        "avis-column-money",
      ),
    ];
  }, [res]);

  const renderContent = (
    <>
      <AvisDescriptiveText textKey={"portal_TDN_Reports"} />
      <AvisFilterableTable<
        BlankMediaTypeCommonDataViewModel,
        BlankMediaTypeApplicationListFilter
      >
        defaultFilterValues={defaultFilterValues}
        filterSessionKey={
          AvisConstants.blank_media_applications_filter_values_key
        }
        filterValidationSchema={validationSchema}
        setError={setError}
        getApiData={(filter) =>
          api.blankMediaUsersGetApplications(filter as any)
        }
        renderFilter={(bag, loading, filtered) => (
          <BlankMediaTypeApplicationsFilterForm
            bag={bag}
            statuses={statuses}
            useTypes={useTypes}
            loading={loading}
            filtered={filtered}
          />
        )}
        tableColumns={columnMetadata}
        tableColumnWidths={[10, 10, 20, 25, 25, 10]}
      />
    </>
  );

  return (
    <AvisForm
      title={res("blankMedia_ApplicationList_Title")}
      error={error}
      loading={isLoadingStatuses || isLoadingUseTypes}
    >
      {renderContent}
    </AvisForm>
  );
}
